import {
  KB_ADMIN_CATEGORIES,
  KB_ADMIN_QUESTIONS,
  KB_ADMIN_QUESTION_GET,
  KB_ADMIN_QUESTION_PUBLISH,
  KB_QUESTION_GET,
  KB_SEARCH,
  KB_SUGGEST,
} from 'src/helpers/url_helper';
import { apiSlice } from 'src/rtk/api/apiSlice';

export const chatbotApi = apiSlice
  .enhanceEndpoints({ addTagTypes: ['KbQuestion', 'KbSearch', 'KbSuggest'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Public: search (no auth)
      kbSearch: builder.query<APIResponse<KbSearchResponse>, { query: string; limit?: number }>({
        query: ({ query, limit = 5 }) => ({
          url: KB_SEARCH,
          params: { query, limit },
        }),
        providesTags: ['KbSearch'],
      }),

      // Public: suggest questions based on input (no auth)
      kbSuggest: builder.query<KbSuggestion[], { query: string; limit?: number }>({
        query: ({ query, limit = 5 }) => ({
          url: KB_SUGGEST,
          params: { query, limit },
        }),
        providesTags: ['KbSuggest'],
      }),
      getKbQuestion: builder.query<APIResponse<KbQuestion>, number | string>({
        query: (id) => ({ url: KB_QUESTION_GET(id) }),
        providesTags: (_result, _err, id) => [{ type: 'KbQuestion', id: String(id) }],
      }),

      // Admin: categories for filters
      getAdminKbCategories: builder.query<APIResponse<{ id: number; name: string }[]>, void>({
        query: () => ({ url: KB_ADMIN_CATEGORIES }),
        providesTags: ['KbQuestion'],
      }),

      // Admin: get one by id
      getAdminKbQuestion: builder.query<APIResponse<KbQuestion>, number | string>({
        query: (id) => ({ url: KB_ADMIN_QUESTION_GET(id) }),
        providesTags: (_result, _err, id) => [{ type: 'KbQuestion', id: String(id) }],
      }),

      // Admin: list with pagination/filters
      getAdminKbQuestions: builder.query<
        APIResponse<{ data: KbQuestion[]; meta: AdminKbListMeta }>,
        AdminKbListParams
      >({
        query: (params) => ({
          url: KB_ADMIN_QUESTIONS,
          params: params as Record<string, string | number | undefined>,
        }),
        providesTags: (result) =>
          result?.data?.data
            ? [
              ...result.data.data.map(({ id }) => ({ type: 'KbQuestion' as const, id })),
              { type: 'KbQuestion', id: 'LIST' },
            ]
            : [{ type: 'KbQuestion', id: 'LIST' }],
      }),

      // Admin: create
      createKbQuestion: builder.mutation<
        APIResponse<KbQuestion>,
        { question: string; answer: string; status?: string; }
      >({
        query: (body) => ({
          url: KB_ADMIN_QUESTIONS,
          method: 'POST',
          body: {
            question: body.question,
            answer: body.answer,
            status: body.status ?? 'draft',
          },
        }),
        invalidatesTags: [{ type: 'KbQuestion', id: 'LIST' }],
      }),

      // Admin: update
      updateKbQuestion: builder.mutation<
        APIResponse<KbQuestion>,
        { id: number | string; question?: string; answer?: string; status?: string; }
      >({
        query: ({ id, ...body }) => ({
          url: KB_ADMIN_QUESTION_GET(id),
          method: 'PUT',
          body: {
            question: body.question,
            answer: body.answer,
            status: body.status,
          },
        }),
        invalidatesTags: (_result, _err, { id }) => [
          { type: 'KbQuestion', id: String(id) },
          { type: 'KbQuestion', id: 'LIST' },
        ],
      }),

      // Admin: delete
      deleteKbQuestion: builder.mutation<APIResponse<{ id: number }>, number | string>({
        query: (id) => ({
          url: KB_ADMIN_QUESTION_GET(id),
          method: 'DELETE',
        }),
        invalidatesTags: [{ type: 'KbQuestion', id: 'LIST' }],
      }),

      // Admin: publish (optional)
      publishKbQuestion: builder.mutation<APIResponse<{ id: number; status: string }>, number | string>({
        query: (id) => ({
          url: KB_ADMIN_QUESTION_PUBLISH(id),
          method: 'PATCH',
        }),
        invalidatesTags: (_result, _err, id) => [
          { type: 'KbQuestion', id: String(id) },
          { type: 'KbQuestion', id: 'LIST' },
        ],
      }),
    }),
  });

export const {
  useKbSearchQuery,
  useLazyKbSearchQuery,
  useKbSuggestQuery,
  useLazyKbSuggestQuery,
  useGetKbQuestionQuery,
  useGetAdminKbCategoriesQuery,
  useGetAdminKbQuestionQuery,
  useGetAdminKbQuestionsQuery,
  useCreateKbQuestionMutation,
  useUpdateKbQuestionMutation,
  useDeleteKbQuestionMutation,
  usePublishKbQuestionMutation,
} = chatbotApi;
